import { CONTROL_ICONS, droneSoundMute, Header } from "@shared/ui";
import React from "react";
import { useTranslation } from "react-i18next";

import "@shared/ui/dist/components/Diagnostics/style.css";
import "../CameraView/CameraView.css";
import "./ControlGuide.css";

interface IGuideItem {
  key: string;
  preview: React.ReactNode;
}

// Same markup and classes as the buttons on the control screen
const IconButton: React.FC<{ icon: string; className?: string }> = ({
  icon,
  className = "",
}) => (
  <button type="button" tabIndex={-1} className={`btn reload ${className}`}>
    <img src={icon} height="25" width="25" alt="" />
  </button>
);

const SwitchButton: React.FC<{ icon: string; checked: boolean }> = ({
  icon,
  checked,
}) => (
  <div className="usage-status-armed">
    <label className="switch-btn">
      <input type="checkbox" checked={checked} readOnly tabIndex={-1} />
      <span className="btn-state round">
        <img src={icon} alt="" />
      </span>
    </label>
  </div>
);

const LightButtons: React.FC = () => (
  <div className="camera-light-menu">
    <button type="button" tabIndex={-1} className="btn reload active">
      <img
        style={{ transform: "scaleX(-1)" }}
        src={CONTROL_ICONS.cameraLight}
        height="25"
        width="25"
        alt=""
      />
      <span>П</span>
    </button>
    <button type="button" tabIndex={-1} className="btn reload">
      <span>З</span>
      <img src={CONTROL_ICONS.cameraLight} height="25" width="25" alt="" />
    </button>
  </div>
);

const ControlGuide: React.FC = () => {
  const { t } = useTranslation();

  const renderRisks = (key: string) => {
    const risks = t(`controlGuide.items.${key}.risks`, {
      returnObjects: true,
      defaultValue: [],
    });
    if (!Array.isArray(risks) || !risks.length) {
      return null;
    }
    return (
      <div className="control-guide-risks">
        <strong>⚠ {t(`controlGuide.items.${key}.risksTitle`)}</strong>
        <ul>
          {risks.map((risk) => (
            <li key={risk}>{risk}</li>
          ))}
        </ul>
      </div>
    );
  };

  const sections: { title: string; items: IGuideItem[] }[] = [
    {
      title: t("controlGuide.sections.after"),
      items: [
        {
          key: "cruiseControl",
          preview: <IconButton icon={CONTROL_ICONS.cruiseControl} />,
        },
        {
          key: "parkingGrid",
          preview: (
            <div className="controller-bottom-actions">
              <IconButton
                icon={CONTROL_ICONS.parkingGrid}
                className="parking-grid-btn"
              />
            </div>
          ),
        },
        { key: "separateLight", preview: <LightButtons /> },
        {
          key: "autopilot",
          preview: <IconButton icon={CONTROL_ICONS.autopilot} />,
        },
        {
          key: "audio",
          preview: (
            <>
              <IconButton icon={CONTROL_ICONS.audioOn} />
              <IconButton icon={CONTROL_ICONS.audioOff} />
            </>
          ),
        },
        {
          key: "microphone",
          preview: (
            <div className="mic-volume-group">
              <input
                type="range"
                min={0}
                max={1}
                step={0.01}
                value={0.3}
                readOnly
                tabIndex={-1}
                className="mic-volume-slider"
                style={{
                  background:
                    "linear-gradient(to right, #f7ce46 30%, #000 30%)",
                }}
              />
              <button type="button" tabIndex={-1} className="btn reload">
                <img src={droneSoundMute} height="38" width="38" alt="" />
              </button>
            </div>
          ),
        },
        {
          key: "recording",
          preview: (
            <div className="recorder-wrapper">
              <button type="button" tabIndex={-1} className="btn reload start-rec" />
              <button type="button" tabIndex={-1} className="btn reload stop-rec" />
              <button type="button" tabIndex={-1} className="btn reload">
                <img src={CONTROL_ICONS.recordPause} height="15" width="15" alt="" />
              </button>
              <button type="button" tabIndex={-1} className="btn reload recording" />
              <button type="button" tabIndex={-1} className="btn reload">
                <img src={CONTROL_ICONS.recordSave} height="20" width="20" alt="" />
              </button>
            </div>
          ),
        },
      ],
    },
    {
      title: t("controlGuide.sections.before"),
      items: [
        {
          key: "remoteControl",
          preview: (
            <SwitchButton icon={CONTROL_ICONS.remoteControl} checked={false} />
          ),
        },
        {
          key: "keyboard",
          preview: <SwitchButton icon={CONTROL_ICONS.keyboard} checked />,
        },
        {
          key: "touchscreen",
          preview: <SwitchButton icon={CONTROL_ICONS.touchscreen} checked />,
        },
        {
          key: "videoQuality",
          preview: (
            <>
              <SwitchButton icon={CONTROL_ICONS.videoMainQuality} checked />
              <SwitchButton
                icon={CONTROL_ICONS.videoEnhancedQuality}
                checked
              />
            </>
          ),
        },
        { key: "speed", preview: <IconButton icon={CONTROL_ICONS.speed} /> },
        {
          key: "cameraLight",
          preview: (
            <>
              <IconButton icon={CONTROL_ICONS.cameraLightOff} />
              <IconButton icon={CONTROL_ICONS.cameraLightOn} />
            </>
          ),
        },
        {
          key: "cameraMode",
          preview: (
            <>
              <IconButton icon={CONTROL_ICONS.dayMode} />
              <IconButton icon={CONTROL_ICONS.nightMode} />
            </>
          ),
        },
        {
          key: "reloadCameras",
          preview: <IconButton icon={CONTROL_ICONS.reloadCameras} />,
        },
        { key: "map", preview: <IconButton icon={CONTROL_ICONS.map} /> },
        {
          key: "reloadGps",
          preview: <IconButton icon={CONTROL_ICONS.reloadGps} />,
        },
        {
          key: "cameraViews",
          preview: (
            <div className="switch-view-camera">
              <button type="button" tabIndex={-1} className="camera-view1 active" />
              <button type="button" tabIndex={-1} className="camera-view2" />
              <button type="button" tabIndex={-1} className="camera-view3" />
            </div>
          ),
        },
        {
          key: "connectionStatus",
          preview: (
            <div className="line-status">
              <span className="line online"></span>
              <span className="line offline"></span>
            </div>
          ),
        },
        {
          key: "exit",
          preview: (
            <button type="button" tabIndex={-1} className="btn exit-camera-view">
              {t("droneControlBottomPanel.buttons.exit")}
            </button>
          ),
        },
      ],
    },
  ];

  return (
    <div className="container">
      <Header />

      <div className="control-guide">
        <h1 className="text-center">{t("controlGuide.title")}</h1>
        <p className="control-guide-intro">{t("controlGuide.intro")}</p>
        <div className="control-guide-note">
          <p>{t("controlGuide.updateNote")}</p>
          <div className="control-guide-contacts">
            <a href="tel:380683026596">+380 68 302 65 96</a>
          </div>
        </div>

        {sections.map((section) => (
          <section key={section.title} className="control-guide-section">
            <h2 className="diagnostic-title">{section.title}</h2>
            <table className="diagnostic-table control-guide-table">
              <thead>
                <tr>
                  <th className="control-guide-preview-col">
                    {t("controlGuide.table.button")}
                  </th>
                  <th className="control-guide-name-col">
                    {t("controlGuide.table.name")}
                  </th>
                  <th>{t("controlGuide.table.description")}</th>
                </tr>
              </thead>
              <tbody>
                {section.items.map((item) => (
                  <tr key={item.key}>
                    <td>
                      <div className="control-guide-preview" aria-hidden="true">
                        {item.preview}
                      </div>
                    </td>
                    <td className="control-guide-name">
                      {t(`controlGuide.items.${item.key}.name`)}
                    </td>
                    <td className="control-guide-description">
                      {t(`controlGuide.items.${item.key}.description`)}
                      {renderRisks(item.key)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        ))}
      </div>
    </div>
  );
};

export default ControlGuide;
